import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useState, useEffect } from 'react';
import { useHistory } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import {
  ACCOUNT_TYPES,
  APPLICANT_TYPES,
  KYC_DOCUMENT_TYPES,
} from '../../util/constants';
import {
  removeFromSessionStorage,
  retrieveFromSessionStorage,
  storeInSessionStorage,
} from '../../util/helpers/sessionStorageHelper';
import {
  ACCOUNT_TYPE,
  ACCOUNT_TYPE_GTM,
  KYC_SELECTED_TYPE,
} from '../../constants/sessionStorage';
import { useApplicantData } from '../../hooks/useApplicantData';
import { getApplicantData } from '../../util/helpers/applicantDataHelper';
import KycLink from './KycLink';
import { PAGES } from '../../constants/pages';
import BaseLayout from '../../components/BaseLayout';
import Header from '../../components/Header';
import DropzoneArea from '../../components/DropzoneArea';
import OnBoardingCta from '../../components/OnBoarding/OnBoardingCta';
import { socureUploadDocument } from '../../store/actions/documents/documentsActions';
import useApplicationUid from '../../hooks/useApplicationUid';
import Paragraph from '../../components/Paragraph/Paragraph';
import themeColors from '../../assets/styles/themeColors';
import { selectSettings } from '../../store/selectors/settingsSelectors';
import useStepPost from '../../hooks/useStepPost';
import useGtmStepHook from '../../util/hooks/useGtmStepHook';
import { gtmStepHelper } from '../../util/helpers/gtmHelper';
import { ACCOUNT_STEP_COMPLETE, ACCOUNT_STEP_VIEW } from '../../util/gtmEvents';
import { CONTENT_TYPE } from '../../constants/localStorage';

const DropzoneWrapper = styled.div`
  max-width: 25rem;
  width: 100%;

  > div {
    > div {
      width: 100%;
    }
  }
`;

const KycUploadOwnershipDocument = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { onSuccess: onSuccessSkip } = useStepPost({
    nextPath: PAGES.KYC_DONE_SKIP,
  });
  const ApplicantData = useApplicantData(APPLICANT_TYPES.BUSINESS);
  const UserApplicantData = useApplicantData();
  const companyName = getApplicantData(ApplicantData, 'Name');
  const accountType = retrieveFromSessionStorage(ACCOUNT_TYPE);
  const selectedType = retrieveFromSessionStorage(KYC_SELECTED_TYPE);
  const dispatch = useDispatch();
  const { ApplicationUid } = useApplicationUid();
  const [errors, setErrors] = useState('');
  const [uploadedDocument, setUploadedDocuments] = useState({
    Document: '',
  });
  const { BrokerName } = useSelector(selectSettings);

  const { gtmStepEvent } = useGtmStepHook();
  const accountGtmType = retrieveFromSessionStorage(ACCOUNT_TYPE_GTM);

  useEffect(() => {
    storeInSessionStorage(CONTENT_TYPE, 'multipart/form-data');
  }, []);

  useEffect(() => {
    gtmStepHelper({
      gtmStepEvent,
      eventName: ACCOUNT_STEP_VIEW,
      stepLabel: 'Kyc ownership',
      accountType: accountGtmType,
      accountName: UserApplicantData?.PersonName,
      accountDateOfBirth: UserApplicantData?.PersonBirthday,
      accountPhone: UserApplicantData?.Phone,
      accountAddress: UserApplicantData?.PersonAddress,
      customParams: {
        timestamp: new Date().toISOString(),
      },
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGtmEvent = () => {
    gtmStepHelper({
      gtmStepEvent,
      eventName: ACCOUNT_STEP_COMPLETE,
      stepLabel: 'Kyc ownership',
      accountType: accountGtmType,
      accountName: UserApplicantData?.PersonName,
      accountDateOfBirth: UserApplicantData?.PersonBirthday,
      accountPhone: UserApplicantData?.Phone,
      accountAddress: UserApplicantData?.PersonAddress,
      customParams: {
        timestamp: new Date().toISOString(),
      },
    });
  };

  const getTitleTag = () => {
    switch (accountType) {
      case ACCOUNT_TYPES.CorporateApplication[1]:
      case ACCOUNT_TYPES.CorporateApplication[3]:
        return t('kycVerification.titleTagCorporateDocument', {
          brokerName: BrokerName,
        });

      case ACCOUNT_TYPES.CorporateApplication[2]:
        return t('kycVerification.titleTagSoleProprietorshipStatement', {
          brokerName: BrokerName,
        });
      case ACCOUNT_TYPES.CorporateApplication[4]:
        return t('kycVerification.titleTagTrustDocuments', {
          brokerName: BrokerName,
        });

      case ACCOUNT_TYPES.CorporateApplication[5]:
        return t('kycVerification.titleTagPartnershipAgreement', {
          brokerName: BrokerName,
        });
      default:
        break;
    }
  };

  const getHeaderContent = () => {
    switch (accountType) {
      case ACCOUNT_TYPES.CorporateApplication[1]:
      case ACCOUNT_TYPES.CorporateApplication[3]:
        return {
          title: t('kycVerification.companyDocs', { company: companyName }),
          subtitle: t('kycVerification.companyDocsDesc'),
        };

      case ACCOUNT_TYPES.CorporateApplication[2]:
        return {
          title: t('kycVerification.soleDocs'),
          subtitle: t('kycVerification.soleDocsDesc'),
        };
      case ACCOUNT_TYPES.CorporateApplication[4]:
        return {
          title: t('kycVerification.trustDocs'),
          subtitle: t('kycVerification.trustDocsDesc'),
        };

      case ACCOUNT_TYPES.CorporateApplication[5]:
        return {
          title: t('kycVerification.partnershipDocs'),
          subtitle: t('kycVerification.partnershipDocsDesc'),
        };

      default:
        break;
    }
  };
  const onSubmit = () => {
    const formData = new FormData();
    formData.append('Document', uploadedDocument.Document);
    formData.append('Caption', KYC_DOCUMENT_TYPES[8]);

    const onSuccess = () => {
      removeFromSessionStorage(KYC_SELECTED_TYPE);
      history.push(PAGES.KYC_VERIFICATION);
      handleGtmEvent();
    };
    const params = {
      personTargetType: selectedType,
      documentType: KYC_DOCUMENT_TYPES[8],
    };

    dispatch(
      socureUploadDocument({
        ApplicationUid,
        params,
        data: formData,
        onSuccess,
        setErrors,
      }),
    );
  };

  const headerContent = getHeaderContent();

  return (
    <BaseLayout
      previousPath={PAGES.KYC_VERIFICATION}
      isKycProcess="true"
      noLogoBox
    >
      <Helmet>
        <title>{getTitleTag()}</title>
      </Helmet>
      <Header
        title={headerContent?.title}
        subtitle={headerContent?.subtitle}
        subTitleMb={24}
      />
      <DropzoneWrapper>
        <DropzoneArea
          onUpload={(file) =>
            setUploadedDocuments({
              ...uploadedDocument,
              Document: file,
            })
          }
        />
      </DropzoneWrapper>
      <Paragraph color={themeColors.colorErrorMessage} variant="normal">
        {errors}
      </Paragraph>
      <OnBoardingCta disabled={!uploadedDocument.Document} onClick={onSubmit} />
      <KycLink
        onClick={() => {
          handleGtmEvent();
          onSuccessSkip();
        }}
      />
    </BaseLayout>
  );
};

export default KycUploadOwnershipDocument;
